<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import {
    Globe,
    Sparkles,
    Check,
    CheckCircle2,
    XCircle,
    Loader2,
    ArrowRight,
    Lock,
    ShieldCheck,
    Palette,
    RefreshCw,
    AlertCircle,
    Copy,
    School,
    GraduationCap,
  } from "lucide-vue-next";

  const props = defineProps({
    schoolData: {
      type: Object,
      default: null,
    },
  });

  const emit = defineEmits(["website-created"]);

  const store = useStore();
  const { showToast } = useGlobalToast();

  const userRole = computed(() => store.state.userRole || "admin");

  const school = computed(() => {
    return (
      props.schoolData ||
      store.state.schools?.[0] ||
      store.state.websiteDetails ||
      {}
    );
  });

  // Base slug computed from school name
  const defaultSlug = computed(() => {
    const name = school.value?.schoolName || "school";
    return (
      name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9 ]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "") || "school"
    );
  });

  const customSlug = ref("");
  const isCheckingSlug = ref(false);
  const slugStatus = ref(null); // { available: boolean, message: string }
  const suggestions = ref([]);
  const isCreating = ref(false);
  const copiedDomain = ref(false);
  let debounceTimer = null;

  const currentSlug = computed(() => {
    const raw = customSlug.value || defaultSlug.value || "school";
    return raw
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  });

  const liveDomain = computed(() => {
    return `${currentSlug.value || "school"}.educanium.com`;
  });

  const sanitizeInput = (val) => {
    if (!val) return "";
    return val
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-");
  };

  const checkAvailability = async (slugToCheck) => {
    const slug = (slugToCheck || currentSlug.value || "").trim();
    if (!slug || slug.length < 2) {
      slugStatus.value = {
        available: false,
        message: "Address must be at least 2 characters",
      };
      return;
    }

    isCheckingSlug.value = true;
    try {
      const res = await store.dispatch("checkWebsiteSlug", slug);
      if (res && res.success) {
        slugStatus.value = {
          available: res.available,
          message: res.available
            ? "Address is available to claim"
            : "Address is already occupied",
        };

        if (
          !res.available &&
          Array.isArray(res.suggestions) &&
          res.suggestions.length > 0
        ) {
          suggestions.value = res.suggestions;
        }
      }
    } catch (err) {
      console.error("Slug check failed:", err);
      slugStatus.value = {
        available: true,
        message: "Ready to launch",
      };
    } finally {
      isCheckingSlug.value = false;
    }
  };

  const onSlugInput = (e) => {
    const raw = e.target.value;
    const clean = sanitizeInput(raw);
    customSlug.value = clean;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      checkAvailability(clean);
    }, 350);
  };

  const selectSuggestion = (suggestion) => {
    customSlug.value = suggestion;
    slugStatus.value = {
      available: true,
      message: "Address is available to claim",
    };
    checkAvailability(suggestion);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(`https://${liveDomain.value}`);
    copiedDomain.value = true;
    showToast("success", "URL copied to clipboard!");
    setTimeout(() => {
      copiedDomain.value = false;
    }, 2000);
  };

  onMounted(() => {
    customSlug.value = defaultSlug.value;
    checkAvailability(defaultSlug.value);
  });

  watch(
    () => defaultSlug.value,
    (newVal) => {
      if (!customSlug.value || customSlug.value === "school") {
        customSlug.value = newVal;
        checkAvailability(newVal);
      }
    },
  );

  const handleCreateWebsite = async () => {
    if (isCreating.value) return;

    if (slugStatus.value && !slugStatus.value.available) {
      showToast(
        "error",
        "Please select an available address before launching your website.",
      );
      return;
    }

    try {
      isCreating.value = true;
      const res = await store.dispatch("createWebsite", {
        slug: currentSlug.value,
      });

      showToast(
        "success",
        `Website Launched Successfully: ${
          res.websiteUrl || `https://${liveDomain.value}`
        }`,
      );

      emit("website-created", res);
    } catch (err) {
      console.error("❌ Failed to create website:", err);
      if (err.response?.data?.slugExists && err.response?.data?.suggestions) {
        suggestions.value = err.response.data.suggestions;
        slugStatus.value = {
          available: false,
          message: "Address already in use. Please select a recommended name.",
        };
      }
      showToast(
        "error",
        err.response?.data?.message ||
          "Failed to create website. Please try again.",
      );
    } finally {
      isCreating.value = false;
    }
  };
</script>

<template>
  <div
    class="max-w-5xl mx-auto py-2 sm:py-6 px-2 sm:px-4 space-y-8 animate-fadeIn"
  >
    <!-- ======================================================== -->
    <!-- 1. STUDIO BRAND HERO                                     -->
    <!-- ======================================================== -->
    <div class="relative text-center max-w-3xl mx-auto pt-2 pb-1">
      <!-- Floating Pill Badge -->
      <div
        class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 shadow-xs mb-4"
      >
        <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
        <span class="tracking-wide uppercase text-[11px] font-bold"
          >Educanium Cloud Web Studio</span
        >
        <span class="text-emerald-300 dark:text-emerald-600">·</span>
        <span>Version 2.0</span>
      </div>

      <!-- Main Headline -->
      <h1
        class="text-3xl sm:text-5xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight leading-tight"
      >
        Deploy Your School's
        <span
          class="bg-gradient-to-r from-indigo-600 via-blue-600 to-emerald-500 bg-clip-text text-transparent"
        >
          Digital Flagship
        </span>
      </h1>

      <p
        class="mt-3 text-sm sm:text-base text-slate-500 dark:text-[#A6B4C8] leading-relaxed max-w-2xl mx-auto font-light"
      >
        Create an official, production-grade web portal for
        <strong class="font-semibold text-slate-800 dark:text-slate-200">{{
          school?.schoolName || "your institution"
        }}</strong
        >. Fast edge hosting, automated SSL security, and real-time
        synchronization with EduManage.
      </p>

      <!-- Key Assurance Badges -->
      <div
        class="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 mt-5 text-xs text-slate-500 dark:text-[#A6B4C8]"
      >
        <div
          class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100/80 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449]"
        >
          <ShieldCheck
            class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400"
          />
          <span>Automated SSL</span>
        </div>
        <div
          class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100/80 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449]"
        >
          <Palette class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
          <span>6 Curated Themes</span>
        </div>
        <div
          class="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100/80 dark:bg-[#18243A] border border-slate-200/70 dark:border-[#263449]"
        >
          <RefreshCw class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          <span>Live EduManage Sync</span>
        </div>
      </div>
    </div>

    <!-- ======================================================== -->
    <!-- 2. INTERACTIVE BROWSER TERMINAL / ADDRESS DOCK           -->
    <!-- ======================================================== -->
    <div
      class="relative bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-3xl shadow-slate-200/50 dark:shadow-black/40 overflow-hidden transition-all duration-300"
    >
      <!-- macOS-Style Window Header Bar -->
      <div
        class="px-5 py-3.5 bg-slate-100/80 dark:bg-[#0B1120] border-b border-slate-200/80 dark:border-[#263449] flex items-center justify-between gap-4"
      >
        <!-- Window Traffic Dots -->
        <div class="flex items-center gap-2 shrink-0">
          <div class="w-3 h-3 rounded-full bg-rose-500/80"></div>
          <div class="w-3 h-3 rounded-full bg-amber-500/80"></div>
          <div class="w-3 h-3 rounded-full bg-emerald-500/80"></div>
          <span
            class="ml-2 text-[11px] font-mono font-medium text-slate-400 dark:text-[#7E8DA5] hidden sm:inline"
          >
            portal-deploy.educanium.net
          </span>
        </div>

        <!-- Center Badge -->
        <div
          class="hidden md:flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] text-[11px] font-semibold text-slate-600 dark:text-[#A6B4C8]"
        >
          <School class="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400" />
          <span>{{ school?.schoolName || "Institution Terminal" }}</span>
        </div>

        <!-- Right Quick Pill -->
        <div
          class="text-[11px] font-semibold text-slate-400 dark:text-[#7E8DA5] shrink-0"
        >
          Global DNS · Edge CDN
        </div>
      </div>

      <!-- Main Terminal Body -->
      <div class="p-6 sm:p-9 lg:p-10 space-y-6">
        <!-- Interactive Address Input Bar -->
        <div class="space-y-2.5">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <span
              class="text-xs font-extrabold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5"
            >
              <Globe class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <span>Claim School Web Address</span>
            </span>

            <!-- Real-time Verification Badge -->
            <div class="flex items-center gap-1 text-xs font-semibold">
              <template v-if="isCheckingSlug">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900/50"
                >
                  <Loader2 class="w-3.5 h-3.5 animate-spin" />
                  <span>Checking availability...</span>
                </span>
              </template>
              <template v-else-if="slugStatus?.available">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50 shadow-xs"
                >
                  <CheckCircle2 class="w-3.5 h-3.5 text-emerald-500" />
                  <span>Available to Claim</span>
                </span>
              </template>
              <template v-else-if="slugStatus && !slugStatus.available">
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/50 shadow-xs"
                >
                  <XCircle class="w-3.5 h-3.5 text-rose-500" />
                  <span>Already Occupied</span>
                </span>
              </template>
            </div>
          </div>

          <!-- Chrome-style Omni-Address Input -->
          <div
            class="relative flex items-center rounded-2xl bg-slate-50 dark:bg-[#0B1120] border-2 transition-all duration-300 shadow-inner group overflow-hidden"
            :class="[
              slugStatus && !slugStatus.available
                ? 'border-rose-300 dark:border-rose-800 focus-within:border-rose-500 ring-2 ring-rose-500/10'
                : slugStatus?.available
                ? 'border-emerald-300 dark:border-emerald-700/80 focus-within:border-emerald-500 ring-2 ring-emerald-500/10'
                : 'border-slate-200 dark:border-[#263449] focus-within:border-indigo-500 ring-2 ring-indigo-500/10',
            ]"
          >
            <!-- Protocol Lock Indicator -->
            <div
              class="px-2.5 sm:px-4 py-2.5 sm:py-4 bg-white/70 dark:bg-[#18243A]/70 text-[11px] sm:text-sm font-mono font-bold text-slate-400 dark:text-[#7E8DA5] border-r border-slate-200/80 dark:border-[#263449] flex items-center gap-1 shrink-0 select-none"
            >
              <Lock class="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span class="hidden sm:inline">https://</span>
            </div>

            <!-- Subdomain Editable Input -->
            <input
              type="text"
              :value="customSlug"
              @input="onSlugInput"
              placeholder="your-school-name"
              class="w-full min-w-0 bg-transparent px-2.5 sm:px-4 py-2.5 sm:py-4 text-sm sm:text-lg font-black font-mono tracking-tight text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none"
            />

            <!-- Subdomain Suffix Badge -->
            <div
              class="px-2.5 sm:px-5 py-2.5 sm:py-4 bg-indigo-50 dark:bg-indigo-950/60 text-[11px] sm:text-base font-extrabold text-indigo-600 dark:text-indigo-400 border-l border-slate-200/80 dark:border-[#263449] select-none shrink-0"
            >
              .educanium.com
            </div>
          </div>

          <!-- Bottom Live URL Status & Copy Button -->
          <div
            class="flex items-center justify-between gap-3 pt-1 text-xs text-slate-500 dark:text-[#A6B4C8]"
          >
            <div class="flex items-center gap-2 truncate">
              <span class="text-slate-400 dark:text-[#7E8DA5]"
                >Public Web Endpoint:</span
              >
              <span
                class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9] truncate"
              >
                https://{{ liveDomain }}
              </span>
            </div>

            <button
              type="button"
              @click="copyUrl"
              class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] text-[11px] font-semibold transition shrink-0 cursor-pointer"
              title="Copy URL"
            >
              <Copy class="w-3 h-3" />
              <span>{{ copiedDomain ? "Copied!" : "Copy" }}</span>
            </button>
          </div>
        </div>

        <!-- ======================================================== -->
        <!-- COLLISION NOTICE & 6 SMART SUGGESTION CARDS             -->
        <!-- ======================================================== -->
        <div
          v-if="!slugStatus?.available || suggestions.length > 0"
          class="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-950/30 dark:via-amber-950/15 border border-amber-500/25 dark:border-amber-900/50 space-y-4 animate-fadeIn"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30"
            >
              <AlertCircle class="w-5 h-5" />
            </div>
            <div class="min-w-0 flex-1">
              <h3
                class="text-sm sm:text-base font-bold text-amber-900 dark:text-amber-200"
              >
                <span v-if="!slugStatus?.available">
                  "{{ currentSlug }}.educanium.com" is currently taken
                </span>
                <span v-else> Recommended available alternatives </span>
              </h3>
              <p
                class="text-xs text-amber-700/90 dark:text-amber-300/80 mt-0.5 leading-relaxed"
              >
                Another school has claimed this exact prefix. Select one of the
                verified alternatives below to claim it with 1-click, or
                customize the address in the box above:
              </p>
            </div>
          </div>

          <!-- 6 Selectable Suggestion Cards Grid -->
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1"
          >
            <button
              v-for="(sug, index) in suggestions"
              :key="index"
              type="button"
              @click="selectSuggestion(sug)"
              class="group relative text-left p-3.5 rounded-2xl border-2 transition-all duration-200 cursor-pointer overflow-hidden flex flex-col justify-between"
              :class="[
                currentSlug === sug
                  ? 'bg-indigo-600 dark:bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/30 ring-2 ring-indigo-500/40 scale-[1.01]'
                  : 'bg-white dark:bg-[#18243A] border-slate-200/90 dark:border-[#263449] hover:border-indigo-400 dark:hover:border-indigo-500 text-slate-800 dark:text-slate-200 hover:shadow-md',
              ]"
            >
              <!-- Card Top -->
              <div class="flex items-center justify-between gap-1 w-full mb-1">
                <span
                  class="text-xs sm:text-sm font-mono font-black truncate tracking-tight"
                  :class="
                    currentSlug === sug
                      ? 'text-white'
                      : 'text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400'
                  "
                >
                  {{ sug }}
                </span>

                <span
                  class="w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition"
                  :class="
                    currentSlug === sug
                      ? 'bg-white text-indigo-600'
                      : 'bg-slate-100 dark:bg-[#111C30] text-slate-400 dark:text-[#7E8DA5] group-hover:bg-indigo-50 group-hover:text-indigo-600'
                  "
                >
                  <Check class="w-3.5 h-3.5" />
                </span>
              </div>

              <!-- Card Bottom -->
              <div class="flex items-center justify-between text-[11px] pt-1">
                <span
                  class="font-mono text-[10px]"
                  :class="
                    currentSlug === sug
                      ? 'text-indigo-100'
                      : 'text-slate-400 dark:text-[#7E8DA5]'
                  "
                >
                  .educanium.com
                </span>

                <span
                  class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider"
                  :class="
                    currentSlug === sug
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50'
                  "
                >
                  Available
                </span>
              </div>
            </button>
          </div>
        </div>

        <!-- ======================================================== -->
        <!-- LIVE SIMULATION MINI PREVIEW                              -->
        <!-- ======================================================== -->
        <div
          class="rounded-2xl border border-slate-200/80 dark:border-[#263449] bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white p-5 sm:p-6 relative overflow-hidden shadow-md"
        >
          <!-- Background Glow Accent -->
          <div
            class="absolute -right-10 -bottom-10 w-48 h-48 bg-blue-500/20 rounded-full blur-2xl pointer-events-none"
          ></div>

          <div
            class="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div class="space-y-1.5">
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
                  ></span>
                  Ready to Provision
                </span>
                <span class="text-xs text-blue-200/80 font-mono">
                  Target: {{ liveDomain }}
                </span>
              </div>

              <h4
                class="text-base sm:text-lg font-bold text-white leading-tight"
              >
                {{ school?.schoolName || "Institutional Portal" }}
              </h4>

              <p class="text-xs text-blue-200/70 max-w-lg">
                Includes admissions intake, announcements board, faculty roster,
                and interactive campus widgets out-of-the-box.
              </p>
            </div>

            <!-- Theme Chip -->
            <div class="shrink-0 text-right sm:text-right">
              <div
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 text-xs font-semibold text-white"
              >
                <Palette class="w-3.5 h-3.5 text-indigo-300" />
                <span>Theme: Modern Campus</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ======================================================== -->
        <!-- PRIMARY DEPLOYMENT CTA DOCK                              -->
        <!-- ======================================================== -->
        <div class="pt-2">
          <template v-if="userRole === 'admin'">
            <div
              class="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-[#0B1120] border border-slate-200/80 dark:border-[#263449]"
            >
              <!-- Target Pill -->
              <div
                class="flex items-center gap-2.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300"
              >
                <span
                  class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"
                ></span>
                <span>Deploying to:</span>
                <span
                  class="font-mono font-extrabold text-indigo-600 dark:text-indigo-400 bg-white dark:bg-[#18243A] px-2.5 py-1 rounded-lg border border-slate-200 dark:border-[#263449]"
                >
                  {{ liveDomain }}
                </span>
              </div>

              <!-- Main Launch Button -->
              <button
                type="button"
                @click="handleCreateWebsite"
                :disabled="
                  isCreating ||
                  isCheckingSlug ||
                  (slugStatus && !slugStatus.available)
                "
                class="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 hover:from-indigo-500 hover:to-blue-500 active:scale-[0.98] text-white font-extrabold text-sm sm:text-base rounded-xl shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/50 transition-all duration-200 flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <span
                  v-if="isCreating"
                  class="flex items-center gap-2.5"
                >
                  <Loader2 class="animate-spin w-4 h-4 text-white" />
                  <span>Deploying Portal & SSL...</span>
                </span>

                <span
                  v-else
                  class="flex items-center gap-2"
                >
                  <Sparkles class="w-4 h-4 text-yellow-300" />
                  <span>Launch School Website</span>
                  <ArrowRight class="w-4 h-4 ml-0.5" />
                </span>
              </button>
            </div>
          </template>

          <template v-else>
            <div
              class="w-full p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-200 text-xs sm:text-sm flex items-center gap-3"
            >
              <Lock class="w-5 h-5 text-amber-500 shrink-0" />
              <span>
                <strong>Administrator Access Required:</strong> Only school
                administrators can initialize and deploy the official public
                website.
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- ======================================================== -->
    <!-- 3. PREMIUM 3-PILLAR FEATURE SHOWCASE                     -->
    <!-- ======================================================== -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
      <!-- Pillar 1 -->
      <div
        class="p-6 rounded-3xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-sm hover:border-indigo-300 dark:hover:border-indigo-800 transition-all duration-200 group"
      >
        <div
          class="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mb-4 border border-indigo-100 dark:border-indigo-900/40 group-hover:scale-105 transition-transform"
        >
          <Palette class="w-5 h-5" />
        </div>
        <h3
          class="text-sm sm:text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] mb-1"
        >
          6 Curated School Themes
        </h3>
        <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
          Switch themes anytime with live preview. Hero sliders, faculty
          showcases, and color schemes adapt with zero content loss.
        </p>
      </div>

      <!-- Pillar 2 -->
      <div
        class="p-6 rounded-3xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-sm hover:border-emerald-300 dark:hover:border-emerald-800 transition-all duration-200 group"
      >
        <div
          class="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4 border border-emerald-100 dark:border-emerald-900/40 group-hover:scale-105 transition-transform"
        >
          <RefreshCw class="w-5 h-5" />
        </div>
        <h3
          class="text-sm sm:text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] mb-1"
        >
          Real-Time Data Sync
        </h3>
        <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
          Notices, upcoming events, teacher directories, and campus updates from
          EduManage sync automatically to your public visitors.
        </p>
      </div>

      <!-- Pillar 3 -->
      <div
        class="p-6 rounded-3xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] shadow-sm hover:border-blue-300 dark:hover:border-blue-800 transition-all duration-200 group"
      >
        <div
          class="w-11 h-11 rounded-2xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center mb-4 border border-blue-100 dark:border-blue-900/40 group-hover:scale-105 transition-transform"
        >
          <GraduationCap class="w-5 h-5" />
        </div>
        <h3
          class="text-sm sm:text-base font-extrabold text-slate-900 dark:text-[#F1F5F9] mb-1"
        >
          Admissions & Custom Domains
        </h3>
        <p class="text-xs text-slate-500 dark:text-[#A6B4C8] leading-relaxed">
          Integrated lead intake form to capture parent inquiries, plus support
          to map your custom domain (e.g. yourschool.edu) anytime.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fadeIn {
    animation: fadeIn 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
</style>
